import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowRight, ArrowLeftRight, Plane, Users, Calendar } from "lucide-react";

import { searchFlights } from "@/lib/travelpayouts.functions";
import { FlightResults } from "./FlightResults";

const AIRPORTS: { code: string; label: string }[] = [
  { code: "MEX", label: "Ciudad de México (MEX)" },
  { code: "CUN", label: "Cancún (CUN)" },
  { code: "GDL", label: "Guadalajara (GDL)" },
  { code: "MTY", label: "Monterrey (MTY)" },
  { code: "MID", label: "Mérida (MID)" },
  { code: "OAX", label: "Oaxaca (OAX)" },
  { code: "PVR", label: "Puerto Vallarta (PVR)" },
  { code: "SJD", label: "Los Cabos (SJD)" },
  { code: "TIJ", label: "Tijuana (TIJ)" },
  { code: "MIA", label: "Miami (MIA)" },
  { code: "JFK", label: "Nueva York (JFK)" },
  { code: "LAX", label: "Los Ángeles (LAX)" },
  { code: "MAD", label: "Madrid (MAD)" },
  { code: "BCN", label: "Barcelona (BCN)" },
  { code: "BOG", label: "Bogotá (BOG)" },
  { code: "LIM", label: "Lima (LIM)" },
];

type SearchParams = {
  origin: string;
  destination: string;
  departDate: string;
  returnDate?: string;
  passengers: number;
};

export function InlineFlightSearch({
  resultsTitle = "Resultados",
}: {
  resultsTitle?: string;
}) {
  const [origin, setOrigin] = useState("");
  const [destination, setDestination] = useState("");
  const [departDate, setDepartDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [oneWay, setOneWay] = useState(false);
  const [passengers, setPassengers] = useState(1);
  const [submitted, setSubmitted] = useState<SearchParams | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const searchFn = useServerFn(searchFlights);

  const queryKey = useMemo(
    () =>
      submitted
        ? [
            "flights",
            submitted.origin,
            submitted.destination,
            submitted.departDate,
            submitted.returnDate ?? "oneway",
            submitted.passengers,
          ]
        : ["flights", "idle"],
    [submitted],
  );

  const m = useQuery({
    queryKey,
    enabled: !!submitted,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: () => searchFn({ data: submitted! }),
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!origin || !destination) {
      setFormError("Selecciona origen y destino.");
      return;
    }
    if (origin === destination) {
      setFormError("Origen y destino deben ser diferentes.");
      return;
    }
    if (!departDate) {
      setFormError("Selecciona la fecha de salida.");
      return;
    }
    if (!oneWay && !returnDate) {
      setFormError("Selecciona la fecha de regreso.");
      return;
    }
    setFormError(null);
    setSubmitted({
      origin,
      destination,
      departDate,
      returnDate: oneWay ? undefined : returnDate,
      passengers,
    });
  }

  function swap() {
    setOrigin(destination);
    setDestination(origin);
  }

  return (
    <div className="space-y-8">
      <form
        onSubmit={onSubmit}
        className="rounded-3xl border border-border bg-card p-4 shadow-card sm:p-5"
      >
        {/* Trip-type + passengers */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full bg-muted p-1 text-xs font-semibold">
            <button
              type="button"
              onClick={() => setOneWay(false)}
              className={`rounded-full px-3 py-1.5 transition ${
                !oneWay ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"
              }`}
            >
              Ida y vuelta
            </button>
            <button
              type="button"
              onClick={() => setOneWay(true)}
              className={`rounded-full px-3 py-1.5 transition ${
                oneWay ? "bg-background text-foreground shadow-sm" : "text-muted-foreground"
              }`}
            >
              Solo ida
            </button>
          </div>
          <label className="ml-auto inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-xs font-semibold">
            <Users className="h-3.5 w-3.5 text-muted-foreground" />
            <select
              value={passengers}
              onChange={(e) => setPassengers(Number(e.target.value))}
              className="border-0 bg-transparent text-xs font-semibold outline-none"
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
                <option key={n} value={n}>
                  {n} {n === 1 ? "pasajero" : "pasajeros"}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Fields */}
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-[1fr_auto_1fr_1fr_1fr_auto] lg:items-end lg:gap-2">
          <Field icon={Plane} label="Origen">
            <AirportSelect value={origin} onChange={setOrigin} />
          </Field>

          <button
            type="button"
            onClick={swap}
            aria-label="Intercambiar origen y destino"
            className="hidden h-11 w-11 items-center justify-center self-end rounded-full border border-border bg-background text-muted-foreground transition hover:border-primary hover:text-primary lg:inline-flex"
          >
            <ArrowLeftRight className="h-4 w-4" />
          </button>

          <Field icon={Plane} label="Destino">
            <AirportSelect value={destination} onChange={setDestination} />
          </Field>

          <Field icon={Calendar} label="Salida">
            <input
              type="date"
              value={departDate}
              onChange={(e) => setDepartDate(e.target.value)}
              className="h-11 w-full border-0 bg-transparent px-1 text-sm font-medium outline-none"
            />
          </Field>

          <Field icon={Calendar} label="Regreso" disabled={oneWay}>
            <input
              type="date"
              value={returnDate}
              disabled={oneWay}
              onChange={(e) => setReturnDate(e.target.value)}
              className="h-11 w-full border-0 bg-transparent px-1 text-sm font-medium outline-none disabled:cursor-not-allowed disabled:opacity-50"
            />
          </Field>

          <button
            type="submit"
            disabled={m.isFetching}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-glow transition-transform hover:scale-[1.02] disabled:opacity-60 sm:col-span-2 lg:col-span-1 lg:h-[68px]"
          >
            {m.isFetching ? "Buscando…" : "Buscar"} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        {formError && (
          <p className="mt-3 text-xs font-medium text-destructive">{formError}</p>
        )}
      </form>


      {(submitted || m.data || m.isError) && (
        <section>
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-display text-2xl font-semibold sm:text-3xl">{resultsTitle}</h2>
              {m.data && (
                <p className="mt-1 text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">
                    {m.data.offers.length}
                  </span>{" "}
                  {m.data.offers.length === 1 ? "vuelo encontrado" : "vuelos encontrados"}{" "}
                  · {submitted?.origin} → {submitted?.destination}
                </p>
              )}
            </div>
          </div>

          <FlightResults
            offers={m.data?.offers ?? []}
            loading={m.isFetching && !m.data}
            error={m.isError ? "No pudimos cargar los vuelos. Intenta de nuevo." : null}
          />

        </section>
      )}
    </div>
  );
}


function Field({
  icon: Icon,
  label,
  children,
  disabled,
}: {
  icon: typeof Plane;
  label: string;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex h-[68px] flex-col gap-0.5 rounded-2xl border border-border bg-background px-4 py-2 transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 ${
        disabled ? "opacity-50" : ""
      }`}
    >
      <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </span>
      <div className="-mt-0.5 min-w-0">{children}</div>
    </label>
  );
}

function AirportSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="h-7 w-full border-0 bg-transparent px-0 text-sm font-semibold text-foreground outline-none"
    >
      <option value="">Selecciona…</option>
      {AIRPORTS.map((a) => (
        <option key={a.code} value={a.code}>
          {a.label}
        </option>
      ))}
    </select>
  );
}
